<template>
  <vue-html2pdf
    :show-layout="false"
    :float-layout="true"
    :enable-download="false"
    :preview-modal="true"
    :filename="pdfFileName"
    :pdf-quality="3"
    :manual-pagination="true"
    pdf-format="A4"
    pdf-orientation="landscape"
    :pdf-content-width="isExpandedView ?'1117px' : '3160px'"
    ref="html2Pdf"
    :htmlToPdfOptions="options"
  >
    <v-container ref="pdfContent" fluid class="black-text pdf-content" :class="pageFormat" slot="pdf-content">
      <v-row class="pdf-item">
        <v-col cols="12" class="padding-25 text-center">
          <img
            alt="Vattenfall Logo"
            :src="logo"
          />
        </v-col>
        <v-col cols="12" class="padding-25">
          <ReportHeader
            :item="item"
            :contract="contract"
            pdfMode
          />
        </v-col>
      </v-row>
      <v-row class="pdf-item">
        <v-col cols="6">
          <ReportFinances
            :report="item.report"
            :chosen-currency="chosenCurrency"
            :currencies="contract.currencies"
            :show-parts="false"
            :parts="parts"
            pdfMode
          />
        </v-col>
        <div v-if="isExpandedView" class="html2pdf__page-break"/>
        <v-col cols="6">
          <ReportNotes
            :comment="item.comment"
            :chosen-currency="chosenCurrency"
            pdfMode
          />
        </v-col>
      </v-row>
      <div v-if="isExpandedView" class="html2pdf__page-break"/>
      <v-row class="pdf-item">
        <v-col cols="12" class="topics-table-container">
          <ReportTopics
            :report="item.report"
            :chosen-currency="chosenCurrency"
            :currencies="contract.currencies"
            :show-parts="false"
            :parts="parts"
            pdfMode
          />
        </v-col>
      </v-row>
      <div v-if="isExpandedView" class="html2pdf__page-break"/>
      <v-row :class="{ 'charts-margin': isExpandedView, 'center': isExpandedView && barChartData.length >= 3  }" >
        <v-col cols="12" :md="chartColumnWidth">
          <ReportChartsDoughnut
            :report="item.report"
            :chosen-currency="chosenCurrency"
            :currencies="contract.currencies"
          />
        </v-col>
        <v-col v-if="!isExpandedView && barChartData.length > 0" cols="12" md="6">
          <BarChart
            :label="`${this.$t('reports.topics.riskCauses')}`"
            :data="barChartData"
            :chosen-currency="chosenCurrency"
          />
        </v-col>
        <v-col v-if="isExpandedView && barChartData.length > 0 && barChartData.length < 3" cols="12" md="6">
          <BarChart
            :label="`${this.$t('reports.topics.riskCauses')}`"
            :data="barChartData"
            :chosen-currency="chosenCurrency"
          />
        </v-col>
      </v-row>
      <div v-if="isExpandedView && barChartData.length >= 3"/>
      <v-row v-if="isExpandedView && barChartData.length >= 3">
        <v-col cols="12">
          <BarChart
            :label="`${this.$t('reports.topics.riskCauses')}`"
            :data="barChartData"
            :chosen-currency="chosenCurrency"
          />
        </v-col>
      </v-row>
      <div v-if="isExpandedView && report.variations.length" class="html2pdf__page-break" />
      <template v-for="type in openVariationsList">
        <v-row :key="`open-${type}`" v-if="getVariationsByType(type, 'variations').length > 0">
          <v-col cols="12">
            <v-card>
              <section
                class="pdf-item variation-table-container">
                <div v-for="(variationsPage, pageIndex) in splitVariationsIntoPages(type, 'variations')"
                  :key="pageIndex">
                  <VariationReportTable
                    :items="variationsPage"
                    :type="type"
                    :chosenCurrency="chosenCurrency"
                    :currencies="contract.currencies"
                    :show-parts="false"
                    :parts="parts"
                    :isMultiCurrency="contract.isMultiCurrency"
                    pdfMode
                    :showTitle="pageIndex == 0"
                    class="pdf-item pa-4"
                  />
                  <div v-if="isExpandedView && pageIndex < splitVariationsIntoPages(type, 'variations').length - 1"
                    class="html2pdf__page-break"/>
                </div>
              </section>
            </v-card>
          </v-col>
        </v-row>
        <div :key="`pagebreak-variations-${type}`" v-if="needsPageBreakAfter(type, 'variations')" class="html2pdf__page-break"/>
        <v-row :key="`finalized-${type}`" v-if="getVariationsByType(type, 'finalisedVariations').length > 0">
          <v-col cols="12">
            <v-card>
              <section
                class="pdf-item variation-table-container">
                <div v-for="(variationsPage, pageIndex) in splitVariationsIntoPages(type, 'finalisedVariations')"
                  :key="pageIndex">
                  <VariationReportTable
                    :items="variationsPage"
                    :type="type"
                    :labelsKey="'reports.finalisedVariations'"
                    :chosenCurrency="chosenCurrency"
                    :currencies="contract.currencies"
                    :show-parts="false"
                    :parts="parts"
                    :isMultiCurrency="contract.isMultiCurrency"
                    pdfMode
                    :showTitle="pageIndex == 0"
                    class="pdf-item pa-4"
                  />
                  <div v-if="isExpandedView && pageIndex < splitVariationsIntoPages(type, 'finalisedVariations').length - 1"
                    class="html2pdf__page-break"/>
                </div>
              </section>
            </v-card>
          </v-col>
        </v-row>
        <div :key="`pagebreak-finalised-${type}`" v-if="needsPageBreakAfter(type, 'finalisedVariations')" class="html2pdf__page-break"/>
      </template>
    </v-container>
  </vue-html2pdf>
</template>

<script>
import { mapState } from 'vuex'
import { DEFAULT_REPORT } from '@/helpers'
import VueHtml2pdf from 'vue-html2pdf'
import logo from '@/assets/pdf-logo.png'
import dayjs from 'dayjs'

const MAX_ROWS_PER_PAGE = 10

export default {
  name: 'ReportPdfTemplate',
  components: {
    VueHtml2pdf
  },
  props: {
    contract: Object,
    reports: {
      type: Array,
      default: () => ([])
    },
    showParts: Boolean,
    pageFormat: String
  },
  data () {
    return {
      openVariationsList: ['variation', 'claim', 'amendment'],
      notesEdit: false,
      logo,
      heightPdfContent: 2233
    }
  },
  computed: {
    parts () {
      return this.item.report.parts.map(part => {
        return {
          ...(this.contract.parts.find(i => i.id === part.id) || {}),
          ...part
        }
      })
    },
    haveMultipleParts () {
      return this.contract.parts.length > 1
    },
    item () {
      const item = this.reports.find(i => Number(this.$route.params.reportId) === i.id) || DEFAULT_REPORT
      return item
    },
    report () {
      return this.item.report
    },
    ...mapState({
      currencies: state => state.currencies
    }),
    chosenCurrency () {
      return this.contract.chosenCurrency
    },
    pdfFileName () {
      const date = dayjs(this.item.report.date).format('YYYY-MM-DD')
      return `report-${date}`
    },
    isExpandedView () {
      return this.pageFormat === 'expanded'
    },
    options () {
      return this.isExpandedView
        ? {
            filename: this.pdfFileName,
            margin: [12, 0, 0, 0],
            jsPDF: {
              orientation: 'landscape',
              format: 'A4'
            }
          }
        : {
            filename: this.pdfFileName,
            html2canvas: {
              useCORS: true,
              width: 3160
            },
            jsPDF: {
              unit: 'px',
              format: [3160, this.heightPdfContent],
              orientation: 'landscape'
            }
          }
    },
    barChartData () {
      const categoryValues = {}
      if (this.report && this.report.finalisedVariations && this.report.finalisedVariations.length > 0) {
        this.report.finalisedVariations
          .filter(variation => variation.category && variation.value !== 0)
          .forEach(variation => {
            if (variation.category in categoryValues) {
              categoryValues[variation.category] += variation.agreedValueCents.sum
            } else {
              categoryValues[variation.category] = variation.agreedValueCents.sum
            }
          })
      }
      const filteredData = Object.keys(categoryValues)
        .map(category => ({
          value: categoryValues[category],
          category
        }))
        .sort((a, b) => b.value - a.value)
      return filteredData
    },
    chartColumnWidth () {
      return this.barChartData.length > 0 ? '6' : '12'
    }
  },
  methods: {
    updateHeightPdfContent () {
      this.heightPdfContent = this.$refs.pdfContent.clientHeight
    },
    getVariationsByType (type, source = 'variations') {
      if (source === 'finalisedVariations') {
        return this.report.finalisedVariations ? this.report.finalisedVariations.filter(i => i.type === type) : []
      }
      return this.report.variations.filter(i => i.type === type)
    },
    splitVariationsIntoPages (type, source = 'variations') {
      const items = this.getVariationsByType(type, source)
      if (!this.isExpandedView || items.length === 0) {
        return items.length > 0 ? [items] : []
      }

      const pages = []
      const increment = items.length >= MAX_ROWS_PER_PAGE ? MAX_ROWS_PER_PAGE : items.length

      for (let i = 0; i < items.length; i += increment) {
        pages.push(items.slice(i, i + increment))
      }
      return pages
    },

    buildTablesArray () {
      const allTables = []
      this.openVariationsList.forEach(t => {
        const variations = this.getVariationsByType(t, 'variations')
        const finalisedVariations = this.getVariationsByType(t, 'finalisedVariations')
        if (variations.length > 0) {
          allTables.push({ type: t, source: 'variations', count: variations.length })
        }
        if (finalisedVariations.length > 0) {
          allTables.push({ type: t, source: 'finalisedVariations', count: finalisedVariations.length })
        }
      })
      return allTables
    },

    getMaxRowsForTableCount (tableCount) {
      if (tableCount === 1) return 10
      if (tableCount === 2) return 9
      if (tableCount === 3) return 7
      if (tableCount >= 4) return 5
      return 10
    },

    getMaxRowsForCompactView (tableCount) {
      if (tableCount === 1) return 20
      if (tableCount === 2) return 19
      if (tableCount === 3) return 18
      if (tableCount === 4) return 17
      if (tableCount === 5) return 16
      if (tableCount >= 6) return 8
      return 18
    },

    findFirstPageBreakIndex (allTables) {
      let cumulativeRows = 0
      let tableCount = 0

      for (let i = 0; i < allTables.length; i++) {
        const table = allTables[i]
        cumulativeRows += table.count
        tableCount++

        if (i < allTables.length - 1) {
          const nextTable = allTables[i + 1]
          const potentialRows = cumulativeRows + nextTable.count
          const potentialTableCount = tableCount + 1
          const maxAllowed = this.getMaxRowsForCompactView(potentialTableCount)

          if (potentialRows > maxAllowed) {
            return i
          }
        }
      }

      return -1
    },

    needsPageBreakAfter (type, source) {
      if (!this.isExpandedView) {
        const allTables = this.buildTablesArray()
        const currentIndex = allTables.findIndex(t => t.type === type && t.source === source)

        if (currentIndex === -1 || currentIndex === allTables.length - 1) return false

        const pageBreakIndex = this.findFirstPageBreakIndex(allTables)

        return currentIndex === pageBreakIndex
      }

      const allTables = this.buildTablesArray()
      const currentIndex = allTables.findIndex(t => t.type === type && t.source === source)

      if (currentIndex === -1 || currentIndex === allTables.length - 1) return false

      let currentPageRows = 0
      let currentPageTableCount = 0

      for (let i = 0; i <= currentIndex; i++) {
        const table = allTables[i]
        const tableRows = table.count >= MAX_ROWS_PER_PAGE
          ? (table.count % MAX_ROWS_PER_PAGE || MAX_ROWS_PER_PAGE)
          : table.count

        if (table.count >= MAX_ROWS_PER_PAGE) {
          currentPageRows = tableRows
          currentPageTableCount = tableRows > 0 ? 1 : 0
          continue
        }

        const newPageRows = currentPageRows + tableRows
        const newPageTableCount = currentPageTableCount + 1
        const maxAllowed = this.getMaxRowsForTableCount(newPageTableCount)

        if (newPageRows > maxAllowed && currentPageTableCount > 0) {
          currentPageRows = tableRows
          currentPageTableCount = 1
        } else {
          currentPageRows = newPageRows
          currentPageTableCount = newPageTableCount
        }
      }

      const nextTable = allTables[currentIndex + 1]
      if (!nextTable) return false

      const nextTableRows = nextTable.count >= MAX_ROWS_PER_PAGE
        ? (nextTable.count % MAX_ROWS_PER_PAGE || MAX_ROWS_PER_PAGE)
        : nextTable.count

      if (nextTable.count >= MAX_ROWS_PER_PAGE) return true

      const newPageRows = currentPageRows + nextTableRows
      const newPageTableCount = currentPageTableCount + 1
      const maxAllowed = this.getMaxRowsForTableCount(newPageTableCount)

      return newPageRows > maxAllowed
    }
  },
  mounted () {
    this.updateHeightPdfContent()
  },
  updated () {
    this.updateHeightPdfContent()
  }
}
</script>

<style lang="scss">
.black-text .v-card__text, .black-text .v-card__title {
  color: black !important;
}

.padding-25 {
  padding: 25px;
}

.charts-margin {
  margin-top: 4rem;
}

.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pdf-content {
  font-family: 'Roboto', sans-serif;
  text-align: left !important;
}

.topics-table-container * {
  font-size: 0.70rem;
}

.topics-table-container h2 {
  font-size: 1.25rem;
}

.variation-table-container * {
  font-size: 0.65rem;
}

.variation-table-container h2 {
  font-size: 1.25rem;
}

.variation-table-container .notes-cell {
  width: 225px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  line-height: 1em;
  max-height: 5em;
}

.text-center {
  text-align: center;
}

.pdf-content .v-card {
  box-shadow: none !important;
}
.pdf-mode .v-card__text, .pdf-mode .v-card__title {
  padding-bottom: 0 !important;
}

.html2pdf__page-break {
   margin-top: -300px
}
</style>
